import { useCallback } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import { fireCmdbErrorAnalytics } from '@atlassian/jira-servicedesk-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { getCmdbGraphObjectUrl } from '@atlassian/jira-servicedesk-cmdb-rest-urls/src/common/constants/graph.tsx';

import type {
	WorkspaceId,
	CmdbObjectId,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';

import type { Response, ObjectGraphService } from './types.tsx';

const getObjectGraphUrl = (
	workspaceId: WorkspaceId,
	objectId: CmdbObjectId,
	depth?: number, // if not provided default to 1 on the backend
	maxOfSameType?: number, // defaults to 50
) => {
	if (depth !== undefined && maxOfSameType !== undefined) {
		return getCmdbGraphObjectUrl(workspaceId, objectId, { depth, maxOfSameType });
	}

	return getCmdbGraphObjectUrl(workspaceId, objectId);
};

export const useObjectGraphService = (): ObjectGraphService => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const request = useCallback(
		({
			objectId,
			workspaceId,
			depth,
			maxOfSameType,
		}: {
			objectId: CmdbObjectId;
			workspaceId: WorkspaceId;
			depth?: number;
			maxOfSameType?: number;
		}) =>
			performGetRequest(getObjectGraphUrl(workspaceId, objectId, depth, maxOfSameType))
				.then((res: Response) => {
					fireTrackAnalytics(createAnalyticsEvent({}), 'fetchObjectGraphData succeeded');
					return res;
				})
				.catch((error) => {
					fireCmdbErrorAnalytics({
						error,
						meta: {
							id: 'fetchObjectGraphData',
							packageName: 'jiraServicedeskInsightObjectServices',
							teamName: 'aurum',
						},
					});
					throw error;
				}),
		[createAnalyticsEvent],
	);

	const {
		loading,
		error,
		data,
		fetch: fetchObjectGraph,
	} = useService<
		Response,
		{
			objectId: CmdbObjectId;
			workspaceId: WorkspaceId;
			depth: number | undefined;
			maxOfSameType: number | undefined;
		}
	>(request);

	return [
		{
			loading,
			error,
			data,
		},
		fetchObjectGraph,
	];
};
